import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";
import { MessageEditor } from "../../components/editor/MessageEditor";
import { adoptForeignImages } from "../../lib/signature-images";
import type { BrandImage } from "../../types";
import { SavedFlash } from "./shared";

/**
 * Per-agent signatures, set by an admin.
 *
 * Admin-only for the whole section, not just its writes: a signature is company
 * correspondence, and an agent seeing an editable box for their own is an
 * invitation to change it.
 */
export function SignatureSettings() {
  const queryClient = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const signatures = useQuery({
    queryKey: queryKeys.signatures,
    queryFn: api.signatures,
  });

  const save = useMutation({
    mutationFn: ({ id, value }: { id: number; value: string }) =>
      api.setSignature(id, value.trim() ? value : null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.signatures });
      setSavedAt(Date.now());
    },
  });

  return (
    <Panel
      collapsible
      title="Signatures"
    >
      <div className="space-y-4">
        {(signatures.data ?? []).map((agent) => (
          <div key={agent.id}>
            <div className="mb-1 flex items-baseline gap-2">
              <span className="text-sm font-medium text-content">
                {agent.name}
              </span>
              <span className="text-xs text-muted">{agent.email}</span>
              <span className="ml-auto">
                <SavedFlash pending={save.isPending} at={savedAt} />
              </span>
            </div>
            <SignatureEditor
              key={agent.id}
              value={agent.signatureHtml ?? ""}
              onSave={(value) => save.mutate({ id: agent.id, value })}
            />
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-faint">
        Paste a signature straight from Gmail, logo and banner included: the
        formatting is kept, and any images that came with it are copied into
        this desk's library so they still load once the mail reaches a
        customer. Left alone they would not — a pasted image points at the
        sending client's own server. Anything that cannot be copied is named
        below the editor, and can be added with the Image button instead. A
        channel-level signature may use {"{{agent.name}}"} and{" "}
        {"{{agent.email}}"}.
      </p>
    </Panel>
  );
}

/**
 * One agent's signature.
 *
 * A real editor rather than the plain textarea this replaces. That box held raw
 * HTML, and pasting a signature copied out of Gmail put the *rendered* text
 * into it -- every tag, and therefore every logo, colour and column, dropped on
 * the way in. There was no way to tell from looking, either: what went in
 * looked like the signature, and what came out was a plain paragraph.
 *
 * The HTML view is kept for the other case, somebody holding real markup that
 * they want to paste as markup.
 */
function SignatureEditor({
  value,
  onSave,
}: {
  value: string;
  onSave: (value: string) => void;
}) {
  const queryClient = useQueryClient();
  const [html, setHtml] = useState(value);
  const [source, setSource] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * Rewriting the content means replacing it, and the surface is uncontrolled
   * -- it reads its content once, on mount. So a rewrite is a remount with a
   * new seed, which is also what puts the caret somewhere sane afterwards.
   */
  const [seed, setSeed] = useState(value);
  const [editorKey, setEditorKey] = useState(0);

  const images = useQuery({
    queryKey: queryKeys.brandImages,
    queryFn: api.brandImages,
    staleTime: 60_000,
  });

  const dirty = html !== value;

  const replaceContent = (next: string): void => {
    setHtml(next);
    setSeed(next);
    setEditorKey((key) => key + 1);
  };

  /**
   * Copy the images that came in with a paste into the desk's own library.
   *
   * Runs on every formatted paste rather than behind a button: the agent has
   * no way to know that the logo they can see is one only they can see, so
   * asking them to decide would be asking the wrong person.
   */
  const adoptPasted = async (pasted: string): Promise<void> => {
    if (!/<img\b/i.test(pasted)) return;

    setBusy("Copying images into the library…");
    setNotice(null);
    try {
      const result = await adoptForeignImages(pasted);
      if (result.adopted > 0) {
        replaceContent(result.html);
        void queryClient.invalidateQueries({ queryKey: queryKeys.brandImages });
      }

      setNotice(
        result.failed.length > 0
          ? `${result.adopted} image${result.adopted === 1 ? "" : "s"} copied. Could not copy: ${result.failed.join("; ")}.`
          : result.adopted > 0
            ? `${result.adopted} image${result.adopted === 1 ? "" : "s"} copied into the library, so they will load for the customer.`
            : null,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not copy the images");
    } finally {
      setBusy(null);
    }
  };

  /** A logo or banner off the clipboard or the desktop, straight into the library. */
  const addFiles = async (
    files: File[],
    insertHtml: (fragment: string) => void,
  ): Promise<void> => {
    setBusy("Uploading…");
    setError(null);
    try {
      for (const file of files) {
        const stored = await api.uploadBrandImage(file);
        insertHtml(imageTag(stored.id, stored.name));
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.brandImages });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {source ? (
        <textarea
          value={html}
          onChange={(event) => setHtml(event.target.value)}
          rows={6}
          spellCheck={false}
          className={cn(
            "w-full resize-y rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-content",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        />
      ) : (
        <MessageEditor
          key={editorKey}
          initialHtml={seed}
          onChange={setHtml}
          uploads={[]}
          onUploadsChange={() => {}}
          onError={setError}
          // A signature has nothing to attach: an attachment rides on one
          // message, and this goes on every message. Files dropped here become
          // library images instead -- see onFiles.
          allowAttachments={false}
          // The whole point -- see the comment above.
          allowRichPaste
          onRichPaste={(pasted) => void adoptPasted(pasted)}
          placeholder="Kind regards,…"
          extraTools={({ insertHtml }) => (
            <ImageInsert
              images={images.data ?? []}
              onPick={(id, name) => insertHtml(imageTag(id, name))}
              onUpload={(file) => void addFiles([file], insertHtml)}
            />
          )}
          // A logo dropped on the editor, or a screenshot pasted into it.
          onFiles={(files, tools) => void addFiles(files, tools.insertHtml)}
        />
      )}

      {busy ? <p className="mt-1 text-xs text-muted">{busy}</p> : null}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {notice ? <p className="mt-1 text-xs text-muted">{notice}</p> : null}

      <div className="mt-1.5 flex items-center gap-2">
        <Button
          size="sm"
          disabled={!dirty || busy !== null}
          onClick={() => {
            onSave(html.trim() ? html : "");
            setError(null);
            setNotice(null);
          }}
        >
          Save signature
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setSource(!source)}>
          {source ? "Back to editor" : "Edit HTML"}
        </Button>
        {dirty ? (
          <span className="text-xs text-amber-600">Unsaved</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The tag a library image goes in as.
 *
 * Always the public route: a logo behind the session guard is a broken image
 * in the customer's inbox, because their mail client has no session and never
 * will. Absolute, because this HTML is read outside the browser that wrote it.
 */
function imageTag(id: number, name: string): string {
  const url = `${window.location.origin}/api/public/images/${id}`;
  return `<img src="${url}" alt="${escapeAttribute(name)}" style="max-width:100%">`;
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

/**
 * Insert one of the desk's images, or add a new one.
 *
 * Upload lives in the same control rather than only in the Images panel above:
 * somebody writing a signature has the logo to hand and no reason to know that
 * adding it is a different screen's job.
 */
function ImageInsert({
  images,
  onPick,
  onUpload,
}: {
  images: BrandImage[];
  onPick: (id: number, name: string) => void;
  onUpload: (file: File) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file);
          event.target.value = "";
        }}
      />
      <select
        value=""
        aria-label="Insert an image"
        title="Insert a logo or banner"
        // mousedown is not prevented here as it is on the format buttons: a
        // native select has to take focus to open. The insert goes through the
        // editor's remembered caret instead.
        onChange={(event) => {
          const picked = event.target.value;
          event.target.value = "";
          if (picked === "upload") {
            fileInput.current?.click();
            return;
          }
          const image = images.find((entry) => entry.id === Number(picked));
          if (image) onPick(image.id, image.name);
        }}
        className="h-7 rounded border-none bg-transparent px-1 text-xs text-muted hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
      >
        <option value="">Image…</option>
        {images.map((image) => (
          <option key={image.id} value={image.id}>
            {image.name}
          </option>
        ))}
        <option value="upload">Upload a logo or banner…</option>
      </select>
    </>
  );
}
